/**
 * ============================================================
 *  SHOPIFY SPEED BOOSTER — Target: 90+ PageSpeed Score
 *  Author  : Performance Script by Claude
 *  Version : 2.0.0
 *  Usage   : Paste in Shopify Admin → Themes → Edit Code →
 *            theme.liquid (just before </body>)
 *            OR add via Settings → Custom HTML
 * ============================================================
 */

(function ShopifySpeedBooster() {
  'use strict';

  /* ─────────────────────────────────────────────
   * 1. RESOURCE HINTS — Preconnect / Prefetch
   *    Tells the browser to connect to key domains
   *    before they are actually needed.
   * ───────────────────────────────────────────── */
  function injectResourceHints() {
    const hints = [
      { rel: 'preconnect', href: 'https://cdn.shopify.com', crossorigin: true },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
      { rel: 'preconnect', href: 'https://monorail-edge.shopifysvc.com' },
      { rel: 'dns-prefetch', href: 'https://cdn.shopify.com' },
      { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
    ];

    const head = document.head;
    hints.forEach(function (hint) {
      // Skip if this hint already exists
      const selector = hint.crossorigin
        ? `link[rel="${hint.rel}"][href="${hint.href}"][crossorigin]`
        : `link[rel="${hint.rel}"][href="${hint.href}"]`;
      if (document.querySelector(selector)) return;

      const link = document.createElement('link');
      link.rel = hint.rel;
      link.href = hint.href;
      if (hint.crossorigin) link.crossOrigin = 'anonymous';
      head.appendChild(link);
    });
  }

  /* ─────────────────────────────────────────────
   * 2. IMAGE LAZY LOADING + SHOPIFY CDN OPTIMISATION
   *    - Native lazy loading on all below-fold images
   *    - Forces WebP format via Shopify's CDN params
   *    - Adds width/height to prevent layout shift (CLS)
   * ───────────────────────────────────────────── */
  function optimiseImages() {
    const images = document.querySelectorAll('img');

    images.forEach(function (img, index) {
      // First image is likely LCP — eager load it
      if (index === 0) {
        img.loading = 'eager';
        img.fetchPriority = 'high';
        img.decoding = 'sync';
      } else {
        if (!img.loading) img.loading = 'lazy';
        img.decoding = 'async';
      }

      // Prevent layout shift — set explicit dimensions if missing
      if (!img.width && img.naturalWidth) img.width = img.naturalWidth;
      if (!img.height && img.naturalHeight) img.height = img.naturalHeight;

      // Upgrade Shopify CDN image URLs to WebP + optimal quality
      if (img.src && img.src.includes('cdn.shopify.com')) {
        img.src = addShopifyCDNParams(img.src);
      }
      if (img.dataset.src && img.dataset.src.includes('cdn.shopify.com')) {
        img.dataset.src = addShopifyCDNParams(img.dataset.src);
      }

      // Upgrade srcset entries
      if (img.srcset) {
        img.srcset = img.srcset
          .split(',')
          .map(function (entry) {
            const parts = entry.trim().split(' ');
            if (parts[0] && parts[0].includes('cdn.shopify.com')) {
              parts[0] = addShopifyCDNParams(parts[0]);
            }
            return parts.join(' ');
          })
          .join(', ');
      }
    });
  }

  function addShopifyCDNParams(url) {
    try {
      const u = new URL(url);
      // Only modify if not already parameterized
      if (!u.searchParams.has('format')) u.searchParams.set('format', 'webp');
      if (!u.searchParams.has('quality')) u.searchParams.set('quality', '75');
      return u.toString();
    } catch (e) {
      return url; // Return original if URL parsing fails
    }
  }

  /* ─────────────────────────────────────────────
   * 3. DEFER NON-CRITICAL JAVASCRIPT
   *    Finds render-blocking <script> tags that
   *    are not deferred/async and fixes them.
   * ───────────────────────────────────────────── */
  function deferNonCriticalScripts() {
    const scripts = document.querySelectorAll('script[src]');
    const CRITICAL_PATTERNS = [
      'shopify.com/s/files/1/0/0/', // Shopify core
      'checkout',
      'analytics',
      'consent',
    ];

    scripts.forEach(function (script) {
      const src = script.src || '';
      const isCritical = CRITICAL_PATTERNS.some(function (p) {
        return src.includes(p);
      });

      if (!isCritical && !script.defer && !script.async) {
        script.defer = true;
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 4. FONT DISPLAY SWAP
   *    Prevents invisible text during font load (FOIT)
   *    by injecting font-display: swap for all
   *    @font-face rules found in stylesheets.
   * ───────────────────────────────────────────── */
  function applyFontDisplaySwap() {
    try {
      Array.from(document.styleSheets).forEach(function (sheet) {
        // Skip cross-origin stylesheets (security restriction)
        try {
          const rules = sheet.cssRules || sheet.rules || [];
          Array.from(rules).forEach(function (rule) {
            if (rule.type === CSSRule.FONT_FACE_RULE) {
              if (!rule.style.getPropertyValue('font-display')) {
                rule.style.setProperty('font-display', 'swap');
              }
            }
          });
        } catch (e) {
          // Cross-origin stylesheet — inject a separate override
          if (sheet.href && sheet.href.includes('fonts.googleapis.com')) {
            injectFontDisplayOverride();
          }
        }
      });
    } catch (e) {
      // Silent fail — non-critical optimisation
    }
  }

  function injectFontDisplayOverride() {
    if (document.getElementById('ssb-font-display')) return;
    const style = document.createElement('style');
    style.id = 'ssb-font-display';
    style.textContent = '@font-face { font-display: swap !important; }';
    document.head.appendChild(style);
  }

  /* ─────────────────────────────────────────────
   * 5. CRITICAL CSS EXTRACTION HELPER
   *    Marks above-fold stylesheets as high priority
   *    and defers below-fold ones using print trick.
   * ───────────────────────────────────────────── */
  function deferNonCriticalCSS() {
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    const CRITICAL_CSS_PATTERNS = ['critical', 'base', 'theme', 'main', 'global'];

    links.forEach(function (link, i) {
      const href = link.href || '';
      const isCritical =
        i === 0 || CRITICAL_CSS_PATTERNS.some(function (p) { return href.includes(p); });

      if (!isCritical) {
        // Print media trick — load non-critical CSS without blocking render
        link.media = 'print';
        link.onload = function () {
          this.media = 'all';
          this.onload = null;
        };
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 6. INTERSECTION OBSERVER LAZY LOADER
   *    True lazy loading for images/iframes that
   *    use data-src (common in Shopify themes).
   * ───────────────────────────────────────────── */
  function setupIntersectionLazyLoad() {
    if (!('IntersectionObserver' in window)) return;

    const lazyObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.dataset.src) {
            el.src = el.dataset.src;
            delete el.dataset.src;
          }
          if (el.dataset.srcset) {
            el.srcset = el.dataset.srcset;
            delete el.dataset.srcset;
          }
          if (el.dataset.backgroundImage) {
            el.style.backgroundImage = `url('${el.dataset.backgroundImage}')`;
            delete el.dataset.backgroundImage;
          }

          el.classList.add('ssb-loaded');
          observer.unobserve(el);
        });
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    document
      .querySelectorAll('img[data-src], iframe[data-src], [data-background-image]')
      .forEach(function (el) { lazyObserver.observe(el); });
  }

  /* ─────────────────────────────────────────────
   * 7. REMOVE / DEFER THIRD-PARTY BLOAT
   *    Defers loading of known heavy third-party
   *    scripts until user interaction or idle time.
   * ───────────────────────────────────────────── */
  function deferThirdParties() {
    const DEFER_PATTERNS = [
      'facebook.net',
      'connect.facebook',
      'tiktok.com',
      'snapchat.com',
      'pinterest.com',
      'twitter.com',
      'hotjar.com',
      'mouseflow.com',
      'intercom.io',
      'tidio.com',
      'gorgias.io',
      'yotpo.com',
      'stamped.io',
      'klaviyo.com/media',
      'judge.me',
    ];

    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach(function (script) {
      const src = script.src || '';
      const shouldDefer = DEFER_PATTERNS.some(function (p) { return src.includes(p); });

      if (shouldDefer && !script.defer && !script.async) {
        const newScript = document.createElement('script');
        newScript.src = src;
        newScript.async = true;
        newScript.defer = true;
        if (script.type) newScript.type = script.type;
        if (script.id) newScript.id = script.id + '-deferred';

        // Replace after first user interaction
        const loadDeferred = function () {
          if (!newScript.parentNode) {
            document.body.appendChild(newScript);
          }
          ['scroll', 'click', 'mousemove', 'keydown', 'touchstart'].forEach(function (ev) {
            window.removeEventListener(ev, loadDeferred, { passive: true });
          });
        };

        ['scroll', 'click', 'mousemove', 'keydown', 'touchstart'].forEach(function (ev) {
          window.addEventListener(ev, loadDeferred, { passive: true });
        });

        // Fallback: load after 5 seconds regardless
        setTimeout(loadDeferred, 5000);

        // Remove original blocking script
        script.parentNode.removeChild(script);
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 8. PRELOAD NEXT PAGE ON LINK HOVER
   *    Speeds up navigation by preloading pages
   *    the user is likely to visit next.
   * ───────────────────────────────────────────── */
  function prefetchOnHover() {
    if (!('IntersectionObserver' in window)) return;

    const prefetched = new Set();

    function prefetch(url) {
      if (prefetched.has(url)) return;
      prefetched.add(url);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }

    document.addEventListener('mouseover', function (e) {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;

      const href = anchor.href;
      // Only prefetch same-origin internal links (not cart/checkout)
      if (
        href &&
        href.startsWith(window.location.origin) &&
        !href.includes('/cart') &&
        !href.includes('/checkout') &&
        !href.includes('/account') &&
        !href.includes('?') &&
        !href.includes('#')
      ) {
        prefetch(href);
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 9. REDUCE CUMULATIVE LAYOUT SHIFT (CLS)
   *    Sets explicit dimensions on images/iframes
   *    without width/height attributes.
   * ───────────────────────────────────────────── */
  function reduceCLS() {
    if (!('IntersectionObserver' in window)) return;

    const clsObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const img = entry.target;
        if (img.naturalWidth && !img.getAttribute('width')) {
          img.setAttribute('width', img.naturalWidth);
          img.setAttribute('height', img.naturalHeight);
        }
        clsObserver.unobserve(img);
      });
    });

    document.querySelectorAll('img:not([width])').forEach(function (img) {
      if (img.complete) {
        if (img.naturalWidth) {
          img.setAttribute('width', img.naturalWidth);
          img.setAttribute('height', img.naturalHeight);
        }
      } else {
        img.addEventListener('load', function () {
          this.setAttribute('width', this.naturalWidth);
          this.setAttribute('height', this.naturalHeight);
        });
      }
      clsObserver.observe(img);
    });

    // Fix iframes without dimensions
    document.querySelectorAll('iframe:not([width])').forEach(function (iframe) {
      if (!iframe.width) iframe.width = '560';
      if (!iframe.height) iframe.height = '315';
    });
  }

  /* ─────────────────────────────────────────────
   * 10. IDLE TASK SCHEDULER
   *     Runs non-urgent tasks during browser idle
   *     time using requestIdleCallback.
   * ───────────────────────────────────────────── */
  function scheduleIdleTasks() {
    const idleFn = window.requestIdleCallback || function (fn) { setTimeout(fn, 1); };

    idleFn(function () {
      applyFontDisplaySwap();
      prefetchOnHover();
    });

    idleFn(function () {
      reduceCLS();
    });
  }

  /* ─────────────────────────────────────────────
   * 11. LCP ELEMENT PRELOAD
   *     Detects the largest image and preloads it
   *     so Largest Contentful Paint fires faster.
   * ───────────────────────────────────────────── */
  function preloadLCPImage() {
    const heroSelectors = [
      '.hero img',
      '.banner img',
      '.slideshow img',
      '.product__media img',
      '.featured-image img',
      'img.hero__image',
      '[data-section-type="image-banner"] img',
      '.product-single__photo img',
    ];

    let heroImg = null;
    for (const sel of heroSelectors) {
      heroImg = document.querySelector(sel);
      if (heroImg) break;
    }
    if (!heroImg) heroImg = document.querySelector('img'); // fallback to first img

    if (heroImg && heroImg.src) {
      heroImg.loading = 'eager';
      heroImg.fetchPriority = 'high';

      const preload = document.createElement('link');
      preload.rel = 'preload';
      preload.as = 'image';
      preload.href = heroImg.src;
      if (heroImg.srcset) preload.imageSrcset = heroImg.srcset;
      if (heroImg.sizes) preload.imageSizes = heroImg.sizes;
      document.head.prepend(preload); // prepend for maximum priority
    }
  }

  /* ─────────────────────────────────────────────
   * 12. REMOVE UNUSED SHOPIFY POLYFILLS
   *     Removes polyfills that modern browsers
   *     already handle natively.
   * ───────────────────────────────────────────── */
  function removeUnnecessaryPolyfills() {
    const POLYFILL_PATTERNS = [
      'polyfill.io',
      'polyfills.js',
      'es6-promise',
      'custom-event-polyfill',
    ];

    // Check if browser is modern
    const isModern =
      'fetch' in window &&
      'Promise' in window &&
      'IntersectionObserver' in window &&
      CSS.supports('display', 'grid');

    if (!isModern) return; // Keep polyfills for older browsers

    document.querySelectorAll('script[src]').forEach(function (script) {
      const src = script.src || '';
      const isPolyfill = POLYFILL_PATTERNS.some(function (p) { return src.includes(p); });
      if (isPolyfill) {
        script.parentNode.removeChild(script);
      }
    });
  }

  /* ─────────────────────────────────────────────
   * 13. VIDEO LAZY LOADING
   *     Defers loading of video elements and
   *     YouTube/Vimeo iframes until visible.
   * ───────────────────────────────────────────── */
  function lazyLoadVideos() {
    if (!('IntersectionObserver' in window)) return;

    const videoObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.tagName === 'VIDEO') {
            el.load();
          } else if (el.tagName === 'IFRAME') {
            if (el.dataset.src) {
              el.src = el.dataset.src;
              delete el.dataset.src;
            }
          }
          observer.unobserve(el);
        });
      },
      { rootMargin: '200px 0px' }
    );

    // Defer video sources
    document.querySelectorAll('video').forEach(function (video) {
      video.preload = 'none';
      video.setAttribute('playsinline', '');
      videoObserver.observe(video);
    });

    // Defer YouTube/Vimeo iframes
    document.querySelectorAll('iframe[src*="youtube"], iframe[src*="vimeo"]').forEach(function (iframe) {
      iframe.dataset.src = iframe.src;
      iframe.src = 'about:blank';
      if (!iframe.getAttribute('loading')) iframe.setAttribute('loading', 'lazy');
      videoObserver.observe(iframe);
    });
  }

  /* ─────────────────────────────────────────────
   * 14. SHOPIFY SECTION RENDERER OPTIMISER
   *     Batches section render calls to avoid
   *     multiple unnecessary re-renders.
   * ───────────────────────────────────────────── */
  function optimiseSectionRenderer() {
    if (!window.Shopify || !window.Shopify.sections) return;

    const originalLoad = window.Shopify.sections && window.Shopify.sections.load;
    if (!originalLoad) return;

    let pendingSections = [];
    let batchTimer = null;

    window.Shopify.sections.load = function (section) {
      pendingSections.push(section);
      clearTimeout(batchTimer);
      batchTimer = setTimeout(function () {
        pendingSections.forEach(function (s) { originalLoad.call(window.Shopify.sections, s); });
        pendingSections = [];
      }, 50);
    };
  }

  /* ─────────────────────────────────────────────
   * INIT — Run all optimisations in correct order
   * ───────────────────────────────────────────── */
  function init() {
    // Phase 1: Run immediately (before DOM is fully parsed)
    injectResourceHints();
    preloadLCPImage();

    // Phase 2: Run after DOM is interactive
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runAfterDOM);
    } else {
      runAfterDOM();
    }
  }

  function runAfterDOM() {
    removeUnnecessaryPolyfills();
    optimiseImages();
    deferNonCriticalScripts();
    deferThirdParties();
    setupIntersectionLazyLoad();
    lazyLoadVideos();
    optimiseSectionRenderer();
    scheduleIdleTasks(); // font swap, hover prefetch, CLS fix

    // Phase 3: Run after full page load
    window.addEventListener('load', function () {
      reduceCLS();
    });
  }

  init();

})();